import { useCallback, useEffect, useState } from "react";
import axios from "../../axios";
import * as S from "../../pages/AdminRoom/Funnel/Funnel.styled";

// Картка учня → «🔎 Пошук нового вчителя»: запит у бот вчителів (усім вчителям мови, крім поточного),
// відгуки «Беру» / «Можу в інший час» → «Перекинути» міняє вчителя. Старому вчителю й учню бот нічого не пише.
const GROUP_LABEL = { kids: "KIDS", teenagers: "TEENS", adults: "ADULTS" };
// Учень чи учениця — здогадка за імʼям, менеджер може перемкнути
const MALE_ON_A = /^(ілля|микола|нікіта|никита|сава|лука|фома|кузьма|данила|ілья|илья|саша)$/i;
const guessFemale = (name) => {
  const first = String(name || "").trim().split(/\s+/)[0];
  return /[аяa]$/i.test(first) && !MALE_ON_A.test(first);
};
const when = (d) => (d ? new Date(d).toLocaleString("uk-UA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const field = { width: "100%", boxSizing: "border-box", font: "inherit", padding: "8px 10px", borderRadius: 10, border: "1px solid #dcd7ee" };

function TeacherSearch({ student, onMoved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    axios.get(`admin/students/${student._id}/teacher-search`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [student._id]);
  useEffect(() => { load(); }, [load]);
  // Поки шукаємо — оновлюємо відгуки
  const searching = data?.request?.status === "searching";
  useEffect(() => {
    if (!searching) return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [searching, load]);

  if (!data || data.error) return null;
  const { request, draft } = data;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openForm = () => { setMessage(""); setForm({ female: guessFemale(draft.name), level: draft.level, schedule: draft.schedule, comment: "" }); };

  const run = async (fn) => {
    setBusy(true); setMessage("");
    try { await fn(); } catch (e) { setMessage("⚠️ " + (e?.response?.data?.message || "Щось пішло не так")); } finally { setBusy(false); }
  };
  const send = () => run(async () => {
    const { data: r } = await axios.post(`admin/students/${student._id}/teacher-search`, form);
    setForm(null);
    setMessage(`✅ Надіслано вчителям: ${r.sentTo}`);
    load();
  });
  const assign = (resp) => {
    const otherTime = resp.kind === "other_time" ? `\n\nВчитель пропонує інший час (${resp.note}) — графік учня не зміниться, поправте його в картці.` : "";
    if (!window.confirm(`Перекинути ${draft.name} до ${resp.teacherName} (ID ${resp.teacherSchoolId})?${otherTime}`)) return;
    run(async () => {
      const { data: r } = await axios.post(`admin/teacher-search/${request._id}/assign`, { teacherId: resp.teacherId });
      onMoved?.(resp.teacherId, r.teacherName);
      load();
    });
  };
  const cancel = () => {
    if (!window.confirm("Скасувати пошук? Вчителям, які відгукнулись, прийде «запит уже не актуальний».")) return;
    run(async () => { await axios.post(`admin/teacher-search/${request._id}/cancel`); load(); });
  };

  const preview = form && [
    `Шукаю викладача для ${form.female ? "регулярної учениці" : "регулярного учня"} ${GROUP_LABEL[student.ageGroup] || ""}:`,
    "",
    `— ${draft.name}`,
    form.level.trim() ? `— ${form.level.trim()}` : null,
    `— ${form.schedule.trim() || "…"} або запропонуйте свій варіант`,
    form.comment.trim() ? `— Коментар: ${form.comment.trim()}` : null,
  ].filter((x) => x !== null).join("\n");

  return (
    <S.Card style={{ margin: "12px 0" }}>
      <S.CardHead>
        <div>
          <S.H>🔎 Пошук нового вчителя</S.H>
          <S.Hint>Запит прийде в бот усім вчителям цієї мови, крім поточного ({draft.teacherName}). Старому вчителю й учню бот нічого не пише.</S.Hint>
        </div>
        {searching && <S.Tag $c="amber">шукаємо вчителя</S.Tag>}
        {request?.status === "assigned" && <S.Tag $c="green">перекинуто</S.Tag>}
      </S.CardHead>

      {!data.botEnabled && <S.Empty>Бот вчителів вимкнений на сервері.</S.Empty>}

      {data.botEnabled && !searching && !form && (
        <S.Btn type="button" onClick={openForm}>Шукати нового вчителя</S.Btn>
      )}

      {form && (
        <div style={{ display: "grid", gap: 10 }}>
          <S.Chips>
            <S.Chip type="button" $on={!form.female} onClick={() => set("female", false)}>учень</S.Chip>
            <S.Chip type="button" $on={form.female} onClick={() => set("female", true)}>учениця</S.Chip>
          </S.Chips>
          <label><S.Small>Рівень</S.Small><input style={field} value={form.level} onChange={(e) => set("level", e.target.value)} /></label>
          <label><S.Small>Дні й час</S.Small><input style={field} value={form.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="вівторок і пʼятниця о 20:00" /></label>
          <label><S.Small>Коментар для вчителів (необовʼязково)</S.Small><input style={field} value={form.comment} onChange={(e) => set("comment", e.target.value)} /></label>
          <S.Small style={{ fontWeight: 700 }}>Так побачать вчителі:</S.Small>
          <div style={{ whiteSpace: "pre-wrap", background: "#f6f4fb", borderRadius: 10, padding: 12, fontSize: "0.9rem" }}>{preview}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <S.Btn type="button" disabled={busy || !form.schedule.trim()} onClick={send}>{busy ? "Надсилаю…" : "Надіслати вчителям"}</S.Btn>
            <S.Btn type="button" $ghost disabled={busy} onClick={() => setForm(null)}>Скасувати</S.Btn>
          </div>
        </div>
      )}

      {request && (
        <div style={{ marginTop: form || !searching ? 12 : 0 }}>
          <S.Hint style={{ margin: "0 0 6px" }}>
            {when(request.createdAt)} · надіслано {request.sent?.length || 0} вчителям · {request.trialWhen}
            {request.status === "cancelled" && " · скасовано"}
          </S.Hint>
          {!request.responses?.length ? (
            <S.Empty>{searching ? "Поки ніхто не відгукнувся" : "Відгуків не було"}</S.Empty>
          ) : (
            <S.List>
              {request.responses.map((r) => {
                const chosen = request.status === "assigned" && String(request.assignedTeacherId) === String(r.teacherId);
                return (
                  <li key={r.teacherId} style={{ alignItems: "center", flexWrap: "wrap" }}>
                    <span>
                      <b>{r.teacherName}</b> <S.Hint as="span" style={{ margin: 0 }}>ID {r.teacherSchoolId} · {when(r.at)}</S.Hint>
                      <S.Hint style={{ margin: 0 }}>{r.kind === "take" ? "✋ бере в цей час" : `🕐 інший час: ${r.note}`}</S.Hint>
                    </span>
                    {searching && <S.Btn type="button" disabled={busy} onClick={() => assign(r)}>Перекинути</S.Btn>}
                    {chosen && <S.Tag $c="green">новий вчитель</S.Tag>}
                  </li>
                );
              })}
            </S.List>
          )}
          {searching && <S.Btn type="button" $ghost disabled={busy} onClick={cancel} style={{ marginTop: 10 }}>Скасувати пошук</S.Btn>}
        </div>
      )}
      {message && <S.Hint style={{ marginTop: 10, fontWeight: 700 }}>{message}</S.Hint>}
    </S.Card>
  );
}

export default TeacherSearch;
